import React from 'react';
import './DiagnosticPopup.css'; // Réutiliser les styles

// 🌈 MÊMES COULEURS QUE LE TORE - Arc-en-ciel THOR 42
const DIAGNOSTIC_COLORS = [];
for (let i = 0; i < 42; i++) {
  const hue = (i / 42) * 360; // Distribution uniforme sur tout le spectre
  const saturation = 1.0; // Saturation maximale
  const lightness = 0.6; // Luminosité optimale pour visibilité
  
  // Convertir HSL en RGB puis en hex
  const h = hue / 360;
  const s = saturation;
  const l = lightness;
  
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = hue2rgb(p, q, h + 1/3);
  const g = hue2rgb(p, q, h);
  const b = hue2rgb(p, q, h - 1/3);
  
  const hexColor = ((1 << 24) + (Math.round(r * 255) << 16) + (Math.round(g * 255) << 8) + Math.round(b * 255)).toString(16).slice(1);
  DIAGNOSTIC_COLORS.push(`#${hexColor}`);
}

const GlobalTherapyPopup = ({ data, onClose, diagnosticData = null, onCaseClick = null }) => {
  if (!data) return null;

  // Position symétrique à gauche
  const popupStyle = {
    position: 'fixed',
    top: '50%',
    left: '20px',
    transform: 'translateY(-50%)',
    maxHeight: '80vh',
    overflowY: 'auto',
    width: '350px'
  };

  // Extraction des données essentielles avec fallbacks
  const globalScore = data.globalScore || data.diagnostic?.globalScore || 85;
  const status = data.status || data.ui?.status || 'Analysé';
  
  const mlPrediction = data.mlPredictions?.prediction || 'sain';
  const mlConfidence = data.mlPredictions?.confidence || 89;
  
  const firstRecommendation = data.recommendations?.[0] || 'Maintenir cette harmonie vocale par des exercices de respiration réguliers';

  // 🎵 CALCUL DU STATUT DES 42 FRÉQUENCES THÉRAPEUTIQUES
  const calculateDiagnosticStatuses = () => {
    const statuses = [];
    
    // 🎵 En mode thérapie, TOUTES les 42 fréquences sont disponibles et colorées !
    for (let i = 0; i < 42; i++) {
      // Utiliser l'index direct pour correspondre EXACTEMENT au popup de fréquence
      // Case 1/42 = index 0, Case 2/42 = index 1, etc.
      
      statuses.push({
        index: i,
        status: 'completed', // Toutes les fréquences sont disponibles
        icon: '', // Pas d'emoji musical
        color: DIAGNOSTIC_COLORS[i] // Couleur directe correspondant au popup de fréquence
      });
    }
    
    return statuses;
  };

  const diagnosticStatuses = calculateDiagnosticStatuses();
  const completedCount = diagnosticStatuses.filter(d => d.status === 'completed').length;
  
  // 🎯 GRILLE DES DIAGNOSTICS (7x6 = 42)
  const renderDiagnosticGrid = () => {
    const rows = [];
    for (let row = 0; row < 6; row++) {
      const cols = [];
      for (let col = 0; col < 7; col++) {
        const index = row * 7 + col;
        const diagnostic = diagnosticStatuses[index];
        
        const handleCaseClick = () => {
          // 🎵 TOUTES les 42 cases thérapeutiques sont cliquables !
          if (onCaseClick) {
            console.log(`🎵 Therapy Dashboard case ${index + 1} clicked`);
            onCaseClick(index);
            // 🎵 NE PAS FERMER LE DASHBOARD - rester ouvert pour naviguer facilement
          }
        };

        cols.push(
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '28px',
              fontSize: '12px',
              cursor: 'pointer', // Toutes les 42 cases thérapeutiques cliquables
              borderRadius: '6px',
              backgroundColor: diagnostic.status === 'completed' ? `${diagnostic.color}80` : // Couleur du tore avec opacité
                             diagnostic.status === 'available' ? 'rgba(108, 117, 125, 0.3)' :   // Gris pour disponible  
                             'rgba(173, 181, 189, 0.2)', // Gris clair pour futur
              border: `1px solid ${diagnostic.color}`,
              position: 'relative',
              fontWeight: 'bold',
              boxShadow: diagnostic.status === 'completed' ? `0 1px 3px ${diagnostic.color}60` : 'none',
              transition: 'all 0.2s ease',
              // Effet de hover pour les cases cliquables
              ':hover': index < 11 ? {
                transform: 'scale(1.1)',
                boxShadow: `0 2px 6px ${diagnostic.color}80`
              } : {}
            }}
            title={`Fréquence ${index + 1}: Thérapeutique THOR 42 - Cliquer pour écouter`}
            onClick={handleCaseClick}
          >
            {diagnostic.icon}
          </div>
        );
      }
      
      rows.push(
        <div key={row} style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
          {cols}
        </div>
      );
    }
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '5px' }}>
        {rows}
      </div>
    );
  };

  return (
    <div className="diagnostic-popup" style={{
      ...popupStyle,
      backgroundColor: 'rgba(20, 20, 30, 0.95)',
      color: 'white',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(10px)'
    }}>
      <button 
        className="popup-close" 
        onClick={onClose}
        style={{ color: 'white', backgroundColor: 'transparent', border: 'none', fontSize: '18px' }}
      >×</button>
      
      {/* En-tête avec score global */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', color: 'white' }}>Dashboard Thérapie Sonore</h3>
        <span style={{ 
          fontSize: '20px', 
          fontWeight: 'bold',
          color: globalScore >= 80 ? '#4caf50' : globalScore >= 60 ? '#ff9800' : '#f44336'
        }}>
          {globalScore}%
        </span>
      </div>

      {/* Progrès global */}
      <div style={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.1)', 
        padding: '8px', 
        borderRadius: '6px', 
        marginBottom: '10px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        textAlign: 'center'
      }}>
        <p style={{ margin: '0', fontSize: '13px', color: 'white', fontWeight: 'bold' }}>
          Progrès : {completedCount}/42 fréquences thérapeutiques
        </p>
      </div>

      {/* Grille des 42 diagnostics */}
      <div style={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.05)', 
        padding: '10px', 
        borderRadius: '6px', 
        marginBottom: '10px',
        border: '1px solid rgba(255, 255, 255, 0.15)'
      }}>
        <h4 style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)', textAlign: 'center' }}>
          Fréquences THOR 42
        </h4>
        {renderDiagnosticGrid()}
        
        {/* Légende */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '15px', 
          marginTop: '8px', 
          fontSize: '10px',
          color: 'rgba(255, 255, 255, 0.7)'
        }}>
          <span><span style={{color: DIAGNOSTIC_COLORS[0]}}>■</span> Complété</span>
          <span><span style={{color: '#6c757d'}}>■</span> Disponible</span>
          <span><span style={{color: '#adb5bd'}}>■</span> Futur</span>
        </div>
      </div>

      {/* Diagnostic IA */}
      <div style={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.1)', 
        padding: '8px', 
        borderRadius: '6px', 
        marginBottom: '8px',
        border: '1px solid rgba(255, 255, 255, 0.2)'
      }}>
        <p style={{ margin: '0', fontSize: '12px', color: 'white' }}>
          <strong>Harmonisation:</strong> {mlPrediction} 
          {mlConfidence > 0 && ` (${Math.round(mlConfidence)}% cohérence)`}
        </p>
      </div>

      {/* Recommandation principale */}
      <div style={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.1)', 
        padding: '8px', 
        borderRadius: '6px',
        border: '1px solid rgba(255, 255, 255, 0.2)'
      }}>
        <h4 style={{ margin: '0 0 4px 0', fontSize: '12px', color: 'white' }}>Recommandation</h4>
        <p style={{ margin: '0', fontSize: '11px', color: 'rgba(255, 255, 255, 0.8)' }}>
          {firstRecommendation}
        </p>
      </div>

      {/* Statut en bas */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: '10px', 
        fontSize: '10px', 
        color: 'rgba(255, 255, 255, 0.6)',
        fontStyle: 'italic'
      }}>
        Cliquer sur le tore pour voir le dashboard
      </div>
    </div>
  );
};

export default GlobalTherapyPopup;